import React, { useEffect, useRef } from 'react';
import { Inbox } from 'lucide-react';
import { useAppDispatch } from '../../../../../app/hooks';
import { useParams, useNavigate } from 'react-router-dom';
import { ROReport, ReportStatus } from '../../../../../api/Reports';
import { JobStatusWithSkeleton } from '../../../../../components/ui/loading/JobStatuswithSkeletonGrid';
import { downloadFormats } from './utils';
import { formatDateForDisplay, getCurrentDate } from '../../../../../lib/utils';
import { handleSendEmail } from '../../../../../services/mailtoGenerator';
import { renderROEmailBody } from '../../../../../services/templating';
import styles from './ROContentContainer.module.css';
import ActionButtonV2 from '../../../../../components/ui/button/v2/ActionButton';
import CansoEditor, { MDXEditorMethods } from '../../../../../components/ui/editor';
import { useAppSelector } from '../../../../../app/store';
import { isAIEnabled, isReportsPolicyNormalizationEnabled } from '../../../../configs/logic/Slice';
import { CaseRouteId, getCaseRoutePath } from '../../../../caselayout/config/caseRoutes';
import { fetchSinglePartyLegacyROReportThunk, fetchSinglePartyROReportThunk } from '../logic/thunks';


interface ROContentContainerProps {
  partyId: string;
  partyName: string;
  noticeData?: ROReport;
  currentContent: string;
  onNoticeChange: (partyId: string, content: string) => void;
  isSaving?: boolean;
  className?: string;
  generateRO: boolean;
}

const ROContentContainer: React.FC<ROContentContainerProps> = ({
  partyId,
  partyName,
  noticeData,
  currentContent,
  onNoticeChange,
  isSaving = false,
  className = '',
  generateRO
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { id: case_id } = useParams<{ id: string }>();
  const isPolicyNormalizationEnabled = useAppSelector(isReportsPolicyNormalizationEnabled);

  const hasContent = currentContent && currentContent.trim().length > 0;

  const isInProgress = noticeData?.status === ReportStatus.SUBMITTED ||
    noticeData?.status === ReportStatus.IN_PROGRESS;

  const shouldDisableButtons = !hasContent || isInProgress || isSaving;

  const isAIFlagEnabled = useAppSelector(isAIEnabled);

  const editorRef = useRef<MDXEditorMethods>(null);

  useEffect(() => {
    if (editorRef.current){
    editorRef.current.setMarkdown(currentContent);
    }
  }, [currentContent]);

  const handleFICRedirect = () => {
    navigate(getCaseRoutePath(case_id!, CaseRouteId.REPORT_FIC));
  };

  const handleRefresh = () => {
    if (case_id && partyId) {
    dispatch(
      isPolicyNormalizationEnabled
        ? fetchSinglePartyROReportThunk({
            case_id,
            report_id: noticeData?.report_id ?? partyId,
            party_id: partyId,
          })

        : fetchSinglePartyLegacyROReportThunk({
            case_id,
            report_id: noticeData?.report_id ?? partyId,
            party_id: partyId,
          })
    );
    }
  };

  const handleSendEmailClick = async () => {
      if (!currentContent.trim() || !partyId) return;

      const subject = `Reasoned Order - ${partyName} - ${formatDateForDisplay(getCurrentDate())}`;
      const signature = "Best regards,\nLegal Department";

      const emailBody = await renderROEmailBody(currentContent, signature);
      handleSendEmail(subject, emailBody);
    };

    const renderTitleAndActions = () => (
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px',
        marginTop: '24px'
      }}>
        <div style={{
          color: '#070B24',
          fontFamily: 'Inter',
          fontSize: '18px',
          fontStyle: 'normal',
          fontWeight: '600',
          lineHeight: '28px',
          marginLeft: '16px',
        }}>
          Reasoned Order Report
        </div>
        <div style={{ display: 'flex', gap: '12px', marginRight: '16px' }}>
          <ActionButtonV2
            variant="secondary"
            size="sm"
            actionType="EMAIL"
            text="Send Email"
            disabled={shouldDisableButtons}
            onClick={handleSendEmailClick}
          />
          <ActionButtonV2
            variant="secondary"
            size="sm"
            actionType="DROPDOWN"
            text="Download"
            disabled={shouldDisableButtons}
            options={downloadFormats}
            onSelect={() => {}}
          />
        </div>
      </div>
  );

  // If we have content, show Markdown editor
  if(!generateRO){
      return (
        <div className={`${styles.container} ${className}`}>
          {/* Header with action buttons */}
          {renderTitleAndActions()}

          <div className={styles.generateContainer}>
            <div className={styles.generateCard}>
              <div className={styles.generateIcon}>
                <Inbox size={24} />
              </div>
              <div className={styles.generateTextContent}>
                <h3 className={styles.generateTitle}>Reasoned Order not yet available</h3>
                <p className={styles.generateDescription}>
                  Reasoned Order is generated based on the instructions provided in Fraud Classification Report.
                </p>
              </div>
              <button
                className={styles.generateButton}
                onClick={handleFICRedirect}
              >
                Review Fraud Classification Report
              </button>
            </div>
          </div>
        </div>
      );
  }

  else {

    if ( isInProgress && isAIFlagEnabled ) {
    return (
      <div className={`${styles.container} ${className}`}>
        {/* Header with action buttons */}
        {renderTitleAndActions()}

        <div className={styles.loadingContainer}>
          <JobStatusWithSkeleton
            title='Request Submitted'
            description='AI agent has started generating the Show Cause Notice. This may take a few minutes to complete.'
          />

          {/* Refresh button section */}
          <div className={styles.refreshSection}>
            <div className={styles.refreshButtonContainer}>
              <button
                className={styles.refreshButton}
                onClick={handleRefresh}
              >
                Refresh Status
              </button>
              <div className={styles.autoRefreshContainer}>
                <div className={styles.autoRefreshIcon}>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`${styles.container} ${className}`}>
      {/* Header with action buttons */}
      {renderTitleAndActions()}

      <div className={styles.editorContainer}>
        <CansoEditor
          ref={editorRef}
          id={`${partyId}-remarks-editor`}
          key={`${partyId}-remarks-editor`}
          markdown={currentContent}
          onChange={(value) => onNoticeChange(partyId, value || '')}
          readOnly={isSaving || false}
          className="border border-gray-300 rounded-md"
          contentEditableClassName='h-[650px]'
          autoHideToolbar={false}
          placeholder='Start writing...'
        />
      </div>
    </div>
  );

 }

};

export default ROContentContainer;
